import type { SourceProvider }                         from './index'
import type { ConnectedSource, FileNode, LocalSource } from '@/tools/exmarked/types'
import { getLocalSource, verifyPermission }             from '@/tools/exmarked/lib/localDb'

// ─── Directory walker ─────────────────────────────────────────────────────────

async function walkDirectory(
  dirHandle: FileSystemDirectoryHandle,
  basePath = '',
): Promise<FileNode[]> {
  const files: FileNode[] = []

  for await (const [name, handle] of dirHandle.entries()) {
    const path = basePath ? `${basePath}/${name}` : name

    if (handle.kind === 'directory') {
      const children = await walkDirectory(handle as FileSystemDirectoryHandle, path)
      files.push(...children)
    } else if (handle.kind === 'file' && /\.mdx?$/i.test(name)) {
      files.push({ name, path, type: 'file', id: path })
    }
  }

  return files
}

// ─── File reader ──────────────────────────────────────────────────────────────

async function readByPath(
  root: FileSystemDirectoryHandle,
  filePath: string,
): Promise<string> {
  const parts = filePath.split('/')
  let dir: FileSystemDirectoryHandle = root

  for (const segment of parts.slice(0, -1)) {
    dir = await dir.getDirectoryHandle(segment)
  }

  const fileHandle = await dir.getFileHandle(parts[parts.length - 1])
  const file       = await fileHandle.getFile()
  return file.text()
}

// ─── Handle retrieval + permission ────────────────────────────────────────────

async function getHandle(id: string): Promise<FileSystemDirectoryHandle> {
  const record = await getLocalSource(id)
  if (!record) {
    throw new Error(
      'Local folder not found — it may have been removed from this device. ' +
      'Disconnect it from the Vault and reconnect to a valid folder.',
    )
  }
  const granted = await verifyPermission(record.handle)
  if (!granted) {
    throw new Error(
      'Permission to read this folder was denied. ' +
      'Return to the Vault and open the source again to re-authorize.',
    )
  }
  return record.handle
}

// ─── Provider ─────────────────────────────────────────────────────────────────

export const localProvider: SourceProvider = {
  async listFiles(source: ConnectedSource) {
    const local  = source as LocalSource
    const handle = await getHandle(local.id)
    const files  = await walkDirectory(handle)
    files.sort((a, b) => a.path.localeCompare(b.path))
    return { files, truncated: false }
  },

  async readFile(source: ConnectedSource, file: FileNode) {
    const local  = source as LocalSource
    const handle = await getHandle(local.id)
    return readByPath(handle, file.path)
  },
}
